import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelScheduled,
  listSentNotifications,
  saveDraftBroadcast,
  searchMerchants,
  sendBroadcast,
} from "./api";
import type { BroadcastInput } from "./types";

const keys = {
  list: ["notifications", "list"] as const,
  merchantSearch: (q: string) => ["notifications", "merchants", q] as const,
};

export function useSentNotifications() {
  return useQuery({
    queryKey: keys.list,
    queryFn: listSentNotifications,
  });
}

export function useSendBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BroadcastInput) => sendBroadcast(input),
    onSuccess: () => {
      // The new broadcast joins the sent-history — refetch the list.
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BroadcastInput) => saveDraftBroadcast(input),
    onSuccess: () => {
      // Real mode persists a DRAFT row (operators see it); refetch so it shows.
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

// Debounced merchant lookup for the SINGLE picker. Disabled until the operator types
// something, so an empty box never fires a request; results are briefly cached per query.
export function useMerchantSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: keys.merchantSearch(q),
    queryFn: () => searchMerchants(q),
    enabled: q.length > 0,
    staleTime: 30_000,
  });
}

export function useCancelScheduled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelScheduled(id),
    onSuccess: () => {
      // The row flips SCHEDULED → CANCELLED — refetch so the badge updates.
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}
